import type { Agreement, LinkState, OrgStatus } from './rules.ts';
import { ORG_STATUS_LABELS, linkLabel } from './rules.ts';
import { ROLE_SOURCE_NAMES, inOrg, isVerified, roleSourcesOf, type RoleSourceName } from './threats.ts';

// R2: Members' four reports (plan 5F), as pure functions of what was read: the guild from one
// snapshot, the profiles, and core's role links. The server reads and the page shows;
// these decide. Each report is made at the viewer's tier, and a column or a row a tier may not
// see isn't built, so it can't be sent by mistake (design §6.12).

export const REPORTS = ['unlinked', 'threats', 'roles', 'guest-anomaly'] as const;
export type ReportName = (typeof REPORTS)[number];

/** The reports a viewer with only `members.view` can have. A threat report needs `members.manage`. */
export const VIEW_REPORTS = ['unlinked', 'roles', 'guest-anomaly'] as const;
export type ViewReportName = (typeof VIEW_REPORTS)[number];

export const REPORT_INFO: Readonly<Record<ReportName, { title: string; about: string; file: string }>> = {
  unlinked: {
    title: 'Unlinked members',
    about: 'Discord members with no profile or no link to an RSI handle, and people on the last complete roster with no Discord account linked.',
    file: 'unlinked-members',
  },
  threats: {
    title: 'Threats',
    about: 'Everyone flagged as a threat, banned or evasive, with why. For members.manage.',
    file: 'threats',
  },
  roles: {
    title: 'Roles',
    about: 'For each Discord role linked from one of Members’ sources: who holds it, and whether that source says they should.',
    file: 'roles',
  },
  'guest-anomaly': {
    title: 'Guest anomalies',
    about: 'Discord members holding two or more roles that are linked from the verified source, who are not verified.',
    file: 'guest-anomalies',
  },
};

export type ReportTier = 'view' | 'manage';

/** A person in the guild, from the snapshot. */
export interface GuildPerson {
  discordId: string;
  name: string;
  roles: readonly string[];
  bot: boolean;
}

/** What the reports read of a profile. */
export interface ReportProfile {
  handle: string;
  discordId: string;
  linkState: LinkState;
  orgStatus: OrgStatus;
  isThreat: boolean;
  permanentThreat: boolean;
  evasive: boolean;
  agreement: Agreement;
  reasons: readonly string[];
  hostileOrgs: readonly string[];
  banReason: string;
  /** The RSI visibility, `Redacted`, `Visible` or blank. */
  visibility: string;
}

/** A role link: a role and what gives it. */
export interface ReportLink {
  roleId: string;
  source: string;
  enabled: boolean;
}

export interface ReportInput {
  guild: readonly GuildPerson[];
  profiles: readonly ReportProfile[];
  links: readonly ReportLink[];
  /** Role names by id. A role missing from it is shown by its id. */
  roleNames: ReadonlyMap<string, string>;
  tier: ReportTier;
}

/** A report as a table: a header, strings, and a line or two about it. */
export interface ReportTable {
  columns: string[];
  rows: string[][];
  summary: { label: string; count: number }[];
  notes: string[];
}

export type Built = { ok: true; table: ReportTable } | { ok: false; reason: string };

/** The sources a viewer may see in the roles report: the others say who is flagged or banned. */
export const VIEW_ROLE_SOURCES: readonly RoleSourceName[] = ['linked', 'verified', 'in_org'];

const byText = (a: string, b: string): number => {
  const [x, y] = [a.toLowerCase(), b.toLowerCase()];
  return x < y ? -1 : x > y ? 1 : a < b ? -1 : a > b ? 1 : 0;
};
const yes = (value: boolean): string => (value ? 'Yes' : 'No');

/** A link's source as a Members source name, or null for another module's or a group's. */
export function membersSource(source: string): RoleSourceName | null {
  if (!source.startsWith('members:')) return null;
  const name = source.slice('members:'.length);
  return (ROLE_SOURCE_NAMES as readonly string[]).includes(name) ? (name as RoleSourceName) : null;
}

const roleName = (names: ReadonlyMap<string, string>, id: string): string => names.get(id) ?? id;

function profilesByDiscord(profiles: readonly ReportProfile[]): Map<string, ReportProfile> {
  const found = new Map<string, ReportProfile>();
  for (const profile of profiles) if (profile.discordId !== '') found.set(profile.discordId, profile);
  return found;
}

/** The profile's facts as the role sources read them. */
const sourceInput = (profile: ReportProfile) => ({ linkState: profile.linkState, orgStatus: profile.orgStatus, isThreat: profile.isThreat, permanentThreat: profile.permanentThreat, agreement: profile.agreement, hostileOrgs: profile.hostileOrgs });

// --- Unlinked ---------------------------------------------------------------------------

/**
 * Two groups in one table. Discord: a member of the guild (not a bot) with no profile, or a profile with no
 * link. RSI only: a profile on the last complete roster, that is a member or an affiliate, with no Discord account.
 */
function unlinked(input: ReportInput): ReportTable {
  const manage = input.tier === 'manage';
  const byId = profilesByDiscord(input.profiles);
  const columns = ['Group', 'Display name', ...(manage ? ['Discord ID'] : []), 'RSI handle', 'Link', 'Org status', ...(manage ? ['Visibility', 'Threat reasons'] : [])];

  const row = (group: string, name: string, discordId: string, profile: ReportProfile | undefined): string[] => [
    group,
    name,
    ...(manage ? [discordId] : []),
    profile?.handle ?? '',
    linkLabel(profile?.linkState ?? ''),
    profile === undefined ? 'Not known' : ORG_STATUS_LABELS[profile.orgStatus],
    ...(manage ? [profile?.visibility ?? '', (profile?.reasons ?? []).join('; ')] : []),
  ];

  const discord = input.guild
    .filter((person) => !person.bot && (byId.get(person.discordId)?.linkState ?? '') === '')
    .sort((a, b) => byText(a.name, b.name) || byText(a.discordId, b.discordId))
    .map((person) => row('Discord', person.name, person.discordId, byId.get(person.discordId)));
  const rsiOnly = input.profiles
    .filter((profile) => profile.discordId === '' && inOrg(profile.orgStatus))
    .sort((a, b) => byText(a.handle, b.handle))
    .map((profile) => row('RSI only', '', '', profile));

  return {
    columns,
    rows: [...discord, ...rsiOnly],
    summary: [
      { label: 'Discord members not linked to an RSI handle', count: discord.length },
      { label: 'RSI org members with no Discord account', count: rsiOnly.length },
    ],
    notes: [],
  };
}

// --- Threats ----------------------------------------------------------------------------

/** Everyone the threat system holds: a flag, a ban or evasion. Names come from the guild snapshot. */
function threats(input: ReportInput): ReportTable {
  const names = new Map(input.guild.map((person) => [person.discordId, person.name]));
  const held = input.profiles.filter((profile) => profile.isThreat || profile.permanentThreat || profile.evasive).sort((a, b) => byText(a.handle, b.handle) || byText(a.discordId, b.discordId));
  const nameOf = (profile: ReportProfile): string => (profile.discordId === '' ? '' : (names.get(profile.discordId) ?? 'Not in the guild'));
  return {
    columns: ['RSI handle', 'Display name', 'Discord ID', 'Link', 'Org status', 'Banned', 'Flagged', 'Evasion', 'Visibility', 'Threat reasons', 'Hostile orgs', 'Ban reason'],
    rows: held.map((profile) => [
      profile.handle,
      nameOf(profile),
      profile.discordId,
      linkLabel(profile.linkState),
      ORG_STATUS_LABELS[profile.orgStatus],
      yes(profile.permanentThreat),
      yes(profile.isThreat),
      yes(profile.evasive),
      profile.visibility,
      profile.reasons.join('; '),
      profile.hostileOrgs.join('; '),
      profile.banReason,
    ]),
    summary: [
      { label: 'Held by the threat system', count: held.length },
      { label: 'Banned', count: held.filter((profile) => profile.permanentThreat).length },
    ],
    notes: [],
  };
}

// --- Roles ------------------------------------------------------------------------------

/**
 * For each link from a Members source: each person in the guild who holds the role or whom the source says should. The source's
 * word comes from the same rules core's role links use (`roleSourcesOf`), applied to the profile read now. Someone with no profile
 * is in no source. A viewer sees only the sources that don't say who is flagged.
 */
function roles(input: ReportInput): ReportTable {
  const manage = input.tier === 'manage';
  const byId = profilesByDiscord(input.profiles);
  const shown = (name: RoleSourceName) => manage || VIEW_ROLE_SOURCES.includes(name);
  const links = input.links
    .map((link) => ({ link, name: membersSource(link.source) }))
    .filter((entry): entry is { link: ReportLink; name: RoleSourceName } => entry.name !== null && shown(entry.name))
    .sort((a, b) => byText(roleName(input.roleNames, a.link.roleId), roleName(input.roleNames, b.link.roleId)) || byText(a.link.source, b.link.source));

  const people = input.guild.filter((person) => !person.bot);
  const rows: string[][] = [];
  const summary: ReportTable['summary'] = [];
  for (const { link, name } of links) {
    const role = roleName(input.roleNames, link.roleId);
    let holders = 0;
    let wrong = 0;
    let missing = 0;
    for (const person of [...people].sort((a, b) => byText(a.name, b.name) || byText(a.discordId, b.discordId))) {
      const profile = byId.get(person.discordId);
      const holds = person.roles.includes(link.roleId);
      const should = profile !== undefined && roleSourcesOf(sourceInput(profile)).includes(name);
      if (!holds && !should) continue;
      if (holds) holders += 1;
      if (holds && !should) wrong += 1;
      if (!holds && should) missing += 1;
      rows.push([
        role,
        `members:${name}`,
        link.enabled ? 'On' : 'Off',
        person.name,
        ...(manage ? [person.discordId] : []),
        profile?.handle ?? '',
        yes(holds),
        yes(should),
        holds ? (should ? 'Holds it, as the source says' : 'Holds it, but the source does not say so') : 'The source says so, but does not hold it',
      ]);
    }
    summary.push({ label: `${role} (members:${name}${link.enabled ? '' : ', link off'}): ${String(holders)} hold it, ${String(wrong)} should not, ${String(missing)} should and do not`, count: holders });
  }
  return {
    columns: ['Role', 'Source', 'Link', 'Display name', ...(manage ? ['Discord ID'] : []), 'RSI handle', 'Holds it', 'Source says so', 'Status'],
    rows,
    summary,
    notes: manage ? [] : ['The sources that say who is flagged or banned are for members.manage, and aren’t listed.'],
  };
}

// --- Guest anomaly ----------------------------------------------------------------------

/**
 * ITCBot's report is people who hold two or more `granted_role_ids` and aren't in the org. Here the granted roles are the roles
 * linked from `members:verified`, so it is: holds two or more of those, and isn't verified (R6). With no such link there is
 * nothing to check, which is a reason, not an empty report.
 */
function guestAnomaly(input: ReportInput): Built {
  const manage = input.tier === 'manage';
  const granted = [...new Set(input.links.filter((link) => membersSource(link.source) === 'verified').map((link) => link.roleId))];
  if (granted.length === 0) return { ok: false, reason: 'No Discord role is linked from the “verified” source, so there are no granted roles to check. Link one on the role links page, then run this again.' };
  const byId = profilesByDiscord(input.profiles);
  const found = input.guild
    .filter((person) => !person.bot)
    .map((person) => ({ person, held: granted.filter((role) => person.roles.includes(role)), profile: byId.get(person.discordId) }))
    .filter(({ held, profile }) => held.length >= 2 && (profile === undefined || !isVerified(sourceInput(profile))))
    .sort((a, b) => byText(a.person.name, b.person.name) || byText(a.person.discordId, b.person.discordId));
  return {
    ok: true,
    table: {
      columns: ['Display name', ...(manage ? ['Discord ID'] : []), 'RSI handle', 'Link', 'Org status', 'Granted roles held', 'Roles'],
      rows: found.map(({ person, held, profile }) => [
        person.name,
        ...(manage ? [person.discordId] : []),
        profile?.handle ?? '',
        linkLabel(profile?.linkState ?? ''),
        profile === undefined ? 'Not known' : ORG_STATUS_LABELS[profile.orgStatus],
        String(held.length),
        held.map((role) => roleName(input.roleNames, role)).join('; '),
      ]),
      summary: [
        { label: 'Not verified, with two or more granted roles', count: found.length },
        { label: 'Granted roles (linked from members:verified)', count: granted.length },
      ],
      notes: [],
    },
  };
}

/** One report, at a tier. A threat report is only ever built for `manage`: at `view` it is refused. */
export function buildReport(name: ReportName, input: ReportInput): Built {
  switch (name) {
    case 'unlinked':
      return { ok: true, table: unlinked(input) };
    case 'threats':
      return input.tier === 'manage' ? { ok: true, table: threats(input) } : { ok: false, reason: 'The threat report is for members.manage' };
    case 'roles':
      return { ok: true, table: roles(input) };
    case 'guest-anomaly':
      return guestAnomaly(input);
  }
}

// --- CSV --------------------------------------------------------------------------------

/**
 * A cell as a spreadsheet will read it. A name is whatever someone chose in Discord, and a cell that begins with `=`, `+`, `-`, `@`, a
 * tab or a return is taken for a formula, so it gets a quote in front: it's shown as typed and never run.
 */
export function csvCell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/** RFC 4180: CRLF between rows, with a byte-order mark so a spreadsheet reads UTF-8 names right. */
export function toCsv(columns: readonly string[], rows: readonly (readonly string[])[]): string {
  return `\uFEFF${[columns, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

/** The download's name: the report, and the day in UTC. */
export function csvFilename(name: ReportName, now: Date): string {
  return `${REPORT_INFO[name].file}-${now.toISOString().slice(0, 10)}.csv`;
}
